import type { IntegrationPlatform } from '@/types'

/**
 * One catalogue for the integration platforms.
 *
 * The list, the logos and the labels used to be restated in five places, and
 * they had drifted: Settings knew about Telegram, the property card did not,
 * the dashboard printed 'Avito'/'Cian'/'Site' in English while every other
 * surface said «Авито»/«Циан». Anything platform-shaped reads from here.
 */

/**
 * Every platform the API can hand back, in display order.
 *
 * Matches `IntegrationPlatform` in the proto and the Prisma enum — a connection
 * or a message can carry any of these.
 */
export const PLATFORMS: IntegrationPlatform[] = ['AVITO', 'CIAN', 'SITE', 'TELEGRAM']

/**
 * The subset that accepts a property listing.
 *
 * Telegram is a messenger integration, not a board: it is a channel for
 * enquiries about a property, so it is deliberately absent from publication
 * targets and publication statistics while staying in `PLATFORMS`.
 */
export const PUBLISH_TARGET_PLATFORMS: IntegrationPlatform[] = ['AVITO', 'CIAN', 'SITE']

/** Platforms that require an account to be connected before publishing. */
export const CONNECTION_REQUIRED_PLATFORMS: ReadonlySet<IntegrationPlatform> = new Set<IntegrationPlatform>(['AVITO', 'CIAN'])

/**
 * Undefined where the platform has no mark of its own — the caller falls back
 * to an icon.
 *
 * SITE carries RentFlow's own mark rather than a generic globe: unlike Avito
 * and Cian it is not somebody else's board, it is our site on our domain, and
 * a placeholder beside two real logos read as a missing asset.
 */
export const PLATFORM_LOGOS: Record<IntegrationPlatform, string | undefined> = {
	AVITO:    '/logos/avito.png',
	CIAN:     '/logos/cian.png',
	SITE:     '/logos/site.svg',
	TELEGRAM: '/logos/telegram.png',
}

/** Logos drawn edge-to-edge rather than padded inside the badge. */
export const PLATFORM_LOGO_FILL: Partial<Record<IntegrationPlatform, boolean>> = {
	CIAN: true,
	// The mark brings its own rounded square, so padding it would box a box.
	SITE: true,
}

/** i18n key for a platform name; lives in `platforms.json`. */
export function platformLabelKey(platform: string): string {
	return `platforms.${platform}`
}

export function isPublishTargetPlatform(platform: string): platform is IntegrationPlatform {
	return (PUBLISH_TARGET_PLATFORMS as string[]).includes(platform)
}

/**
 * The logo for a platform named by a plain string.
 *
 * `Deal.source` and `ExternalListing.platform` arrive as strings, so callers
 * cannot index the record directly. Returning undefined for an unknown name is
 * the point: a platform we have no mark for falls back to an icon.
 */
export function platformLogo(platform: string): string | undefined {
	return PLATFORM_LOGOS[platform as IntegrationPlatform]
}

/** Companion to `platformLogo` for callers holding a plain string. */
export function platformLogoFill(platform: string): boolean | undefined {
	return PLATFORM_LOGO_FILL[platform as IntegrationPlatform]
}
